<script lang="ts">
  import { onMount } from "svelte";
  import { api, type AiState } from "@/api";
  import { token } from "@/stores/session";
  const { serverId }: { serverId: string } = $props();
  let chat = $state<AiState | null>(null);
  let message = $state("");
  let error = $state("");
  let busy = $state(false);
  let sending = $state(false);
  let loading = $state(true);
  let clearRequested = $state(false);
  let messagesElement = $state<HTMLDivElement | null>(null);
  let disposed = false;
  let connected = $state(true);
  let connectionError = $state("");
  let schedulePoll: ((delay: number) => void) | null = null;
  let refreshSequence = 0;
  const actionNames: Record<string, string> = {
    context: "Состояние сервера",
    logs: "Логи",
    list_files: "Каталог",
    search_files: "Поиск по файлам",
    file_changes: "История изменений",
    create_directory: "Создание каталога",
    copy_file: "Копирование",
    move_file: "Перемещение",
    delete_file: "Удаление",
    read_file: "Чтение",
    write_file: "Запись",
    edit_file: "Правка",
    console: "Команда",
    wait: "Ожидание",
    start: "Запуск",
    stop: "Остановка",
    restart: "Перезапуск",
  };
  const active = $derived(
    chat?.tasks.find(
      (t) =>
        t.status === "running" || t.status === "awaiting_confirmation" || t.status === "cancelling",
    ),
  );
  const exhausted = $derived(
    chat !== null &&
      (chat.quota.inputTokensRemaining === 0 || chat.quota.outputTokensRemaining === 0),
  );
  const chatReady = $derived.by(() => {
    const connectedAndIdle = !loading && connected && !active;
    return connectedAndIdle && chat?.configured && !exhausted;
  });
  const inputDisabled = $derived.by(() => {
    const taskBusy = busy || !!active;
    return taskBusy || !chat?.configured || exhausted;
  });
  const sendDisabled = $derived(inputDisabled || !message.trim());
  const working = $derived(
    sending || active?.status === "running" || active?.status === "cancelling",
  );
  const statusLabel = $derived(
    loading
      ? "Подключение…"
      : !connected
        ? "Нет связи с панелью"
        : sending || active?.status === "running"
          ? "Работает…"
          : active?.status === "cancelling"
            ? "Останавливается…"
            : active
              ? "Приостановлен"
              : !chat?.configured
                ? "Не подключён"
                : exhausted
                  ? "Лимит исчерпан"
                  : "Готов к сообщению",
  );
  const inputPercent = $derived(
    chat?.quota.inputTokensLimit
      ? Math.round((chat.quota.inputTokensRemaining / chat.quota.inputTokensLimit) * 100)
      : 0,
  );
  const outputPercent = $derived(
    chat?.quota.outputTokensLimit
      ? Math.round((chat.quota.outputTokensRemaining / chat.quota.outputTokensLimit) * 100)
      : 0,
  );

  $effect(() => {
    const last = chat?.tasks.at(-1);
    const change = `${last?.id}:${last?.status}:${last?.answer}`;
    if (change && messagesElement) {
      messagesElement.scrollTop = messagesElement.scrollHeight;
    }
  });
  async function refresh(signal?: AbortSignal) {
    if (!$token || disposed) {
      return;
    }
    const sequence = ++refreshSequence;
    try {
      const result = await api.aiState($token, serverId, signal);
      if (!disposed && sequence === refreshSequence) {
        chat = result;
        connected = true;
        connectionError = "";
      }
    } catch (e) {
      if (disposed || sequence !== refreshSequence) {
        return;
      }
      connected = false;
      connectionError = e instanceof Error ? e.message : "Не удалось загрузить чат";
      throw e;
    }
  }
  onMount(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    schedulePoll = (delay) => {
      clearTimeout(timer);
      if (!disposed) {
        timer = setTimeout(() => void poll(), delay);
      }
    };
    async function poll() {
      try {
        await refresh(controller.signal);
      } catch {
        /* refresh records connection errors only for the latest request. */
      } finally {
        loading = false;
        schedulePoll?.(active || !connected ? 1500 : 15_000);
      }
    }
    void poll();
    return () => {
      disposed = true;
      schedulePoll = null;
      controller.abort();
      clearTimeout(timer);
    };
  });
  async function act(kind: "send" | "cancel" | "confirm" | "clear") {
    if (!$token || busy) {
      return;
    }
    if (kind === "clear" && (!clearRequested || active)) {
      return;
    }
    busy = true;
    sending = kind === "send";
    error = "";
    try {
      if (kind === "send") {
        await api.aiSend($token, serverId, message.trim());
        message = "";
      } else if (kind === "clear") {
        await api.aiClear($token, serverId);
        clearRequested = false;
      } else if (active) {
        if (kind === "cancel") {
          await api.aiCancel($token, serverId, active.id);
        } else {
          await api.aiConfirm($token, serverId, active.id);
        }
      }
      await refresh();
    } catch (e) {
      error = e instanceof Error ? e.message : "Не удалось выполнить действие";
    } finally {
      busy = false;
      sending = false;
      schedulePoll?.(250);
    }
  }
</script>

<section class="panel-card ai-chat" aria-label="Помощник сервера">
  <div class="ai-heading">
    <div class="ai-title">
      <h2>Помощник</h2>
      <span
        class="ai-status"
        class:working={working && connected}
        class:ready={chatReady}
        role="status"><span class="ai-status-dot" aria-hidden="true"></span>{statusLabel}</span
      >
    </div>
    {#if chat}
      <div class="ai-context">
        <span
          class="ai-quota"
          title="Входящий лимит учитывает скидку на кеш, подтверждённый провайдером. Во время работы часть лимита резервируется."
          >Осталось: вход {inputPercent}% · выход {outputPercent}%<br />Сброс {new Date(
            chat.quota.resetsAt,
          ).toLocaleString("ru-RU", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}</span
        >
        <div class="ai-context-actions">
          <span>Контекст чата</span>
          <button
            class="ai-clear"
            disabled={busy || !!active || !chat.tasks.length}
            onclick={() => (clearRequested = !clearRequested)}
            title={active
              ? "Сначала остановите текущую задачу"
              : "Очистить историю и контекст чата"}
            aria-expanded={clearRequested}>Очистить</button
          >
        </div>
      </div>
    {/if}
  </div>
  {#if clearRequested}
    <div class="ai-clear-confirm">
      <p>Удалить историю и контекст чата?</p>
      <div class="ai-actions">
        <button class="button secondary" disabled={busy} onclick={() => (clearRequested = false)}
          >Отмена</button
        >
        <button class="button" disabled={busy || !!active} onclick={() => act("clear")}
          >{busy ? "Удаление…" : "Удалить"}</button
        >
      </div>
    </div>
  {/if}
  {#if loading}<p role="status">Загрузка…</p>
  {:else if chat && !chat.configured}<p>ИИ ещё не подключён.</p>{/if}
  <div class="ai-messages" bind:this={messagesElement} aria-live="polite">
    {#if chat?.configured && !chat.tasks.length}<p class="ai-empty">
        Опиши, что настроить или исправить. Можно уточнять и продолжать диалог.
      </p>{/if}
    {#each chat?.tasks ?? [] as task (task.id)}
      <article class="ai-turn">
        <div class="ai-user">
          <span class="ai-label">Вы</span>
          <p>{task.prompt}</p>
        </div>
        <div class="ai-assistant">
          <span class="ai-label">Помощник</span>
          {#if task.status === "cancelling"}<p role="status">Останавливаю…</p>
          {:else if task.status === "running"}<p role="status">{task.answer || "Работаю…"}</p>
          {:else if task.status === "awaiting_confirmation"}<p>Нужно подтверждение:</p>
            <pre>{task.confirmation}</pre>
          {:else}<p>{task.answer}</p>{/if}
          {#if task.actions?.length}
            <details class="ai-audit">
              <summary>Действия · {task.actions.length}</summary>
              <ul>
                {#each task.actions as action, actionIndex (actionIndex)}<li>
                    {actionNames[action.tool] ?? action.tool}{action.target
                      ? `: ${action.target}`
                      : ""} — {action.result === "verified"
                      ? "проверено"
                      : action.result === "error"
                        ? "ошибка"
                        : action.result === "attempted" || action.result === "unverified"
                          ? "результат не подтверждён"
                          : "получен ответ"}
                  </li>{/each}
              </ul>
            </details>
          {/if}
        </div>
      </article>
    {/each}
  </div>
  {#if active}
    <div class="ai-actions">
      {#if active.status === "awaiting_confirmation"}<button
          class="button"
          disabled={busy}
          onclick={() => act("confirm")}>Подтвердить</button
        >{/if}
      <button
        class="button secondary"
        disabled={busy || active.status === "cancelling"}
        onclick={() => act("cancel")}
        >{active.status === "awaiting_confirmation" ? "Отменить" : "Остановить"}</button
      >
    </div>
  {/if}
  {#if error}<p class="ai-error" role="alert">{error}</p>{/if}
  {#if connectionError}<p class="ai-error" role="alert">{connectionError}</p>{/if}
  {#if exhausted && !active}<p>Лимит на 5 часов исчерпан.</p>{/if}
  <form
    onsubmit={(event) => {
      event.preventDefault();
      void act("send");
    }}
  >
    <label for="ai-message" class="ai-label">Сообщение помощнику</label>
    <textarea
      id="ai-message"
      bind:value={message}
      maxlength="4000"
      rows="3"
      placeholder="Например: проверь, почему сервер не запускается"
      disabled={inputDisabled}></textarea>
    <button class="button" type="submit" disabled={sendDisabled}
      >{busy ? "Подождите…" : "Отправить"}</button
    >
  </form>
</section>

<style>
  .ai-chat {
    display: grid;
    gap: 1rem;
    min-width: 0;
    padding: 1.5rem;
  }
  .ai-heading {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: start;
  }
  h2,
  p {
    margin: 0;
  }
  h2 {
    font: 500 1.2rem/1.4 var(--font-display);
  }
  .ai-quota {
    font-size: 0.8rem;
    text-align: right;
    opacity: 0.7;
  }
  .ai-messages {
    display: grid;
    gap: 1.25rem;
    max-height: 60vh;
    overflow-y: auto;
    overflow-wrap: anywhere;
  }
  .ai-turn {
    display: grid;
    gap: 0.75rem;
  }
  .ai-user,
  .ai-assistant {
    padding: 0.85rem;
    border: 1px solid var(--line);
  }
  .ai-user {
    background: var(--surface-raised);
  }
  .ai-label {
    display: block;
    font-size: 0.8rem;
    opacity: 0.65;
    margin-bottom: 0.4rem;
  }
  .ai-turn p {
    white-space: pre-wrap;
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font: inherit;
    margin: 0.5rem 0 0;
    max-height: 12rem;
    overflow-y: auto;
  }
  .ai-title {
    display: grid;
    gap: 0.5rem;
  }
  .ai-status {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    border: 1px solid var(--line-strong);
    font-size: 0.8rem;
    color: var(--cream);
  }
  .ai-status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #9ca3af;
    flex-shrink: 0;
  }
  .ai-status.ready .ai-status-dot {
    background: #4ade80;
  }
  .ai-status.working {
    background: #f59e0b18;
    border-color: #f59e0b;
    color: #fbbf24;
  }
  .ai-status.working .ai-status-dot {
    background: #fbbf24;
    animation: ai-pulse 1.2s ease-in-out infinite;
  }
  @keyframes ai-pulse {
    50% {
      opacity: 0.35;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .ai-status.working .ai-status-dot {
      animation: none;
    }
  }
  .ai-context {
    display: grid;
    gap: 0.4rem;
    justify-items: end;
  }
  .ai-context-actions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.75rem;
  }
  .ai-context-actions span {
    opacity: 0.65;
  }
  .ai-clear {
    padding: 0.2rem 0.45rem;
    font: inherit;
    color: var(--cream);
    background: transparent;
    border: 1px solid var(--line-strong);
    cursor: pointer;
  }
  .ai-clear:focus-visible {
    outline: 1px solid var(--mars-light);
  }
  .ai-clear:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .ai-clear-confirm {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding: 0.85rem;
    border: 1px solid var(--line-strong);
  }
  .ai-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  form {
    display: grid;
    gap: 0.75rem;
  }
  textarea {
    box-sizing: border-box;
    width: 100%;
    resize: vertical;
    min-height: 5rem;
    font: inherit;
    color: var(--cream);
    background: var(--ink);
    border: 1px solid var(--line-strong);
    padding: 0.85rem;
  }
  textarea:focus {
    outline: 1px solid var(--mars-light);
  }
  textarea:disabled {
    opacity: 0.52;
    cursor: not-allowed;
  }
  .secondary {
    background: transparent;
    border-color: var(--line-strong);
  }
  form button {
    justify-self: end;
  }
  .ai-error {
    color: #f87171;
  }
  .ai-empty {
    opacity: 0.65;
    padding: 1rem 0;
  }
  .ai-audit {
    margin-top: 0.65rem;
    font-size: 0.8rem;
    opacity: 0.8;
  }
  .ai-audit summary {
    cursor: pointer;
  }
  .ai-audit ul {
    padding-left: 1.25rem;
    margin: 0.5rem 0 0;
  }
  @media (max-width: 480px) {
    .ai-chat {
      padding: 1rem;
    }
    .ai-heading {
      flex-direction: column;
    }
    .ai-quota {
      text-align: left;
    }
    .ai-context {
      justify-items: start;
    }
    form button {
      width: 100%;
    }
  }
</style>
